import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";

const otpSchema = yup.object().shape({
  otp: yup
    .string()
    .required("OTP is required")
    .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
});

function OtpConfirmation() {
  const navigate = useNavigate();
  const { verifyOtp, resendOtp } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const otpToken = searchParams.get("token");
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(otpSchema),
  });

  const mutation = useMutation({
    mutationFn: ({ data }) => {
      return verifyOtp(data);
    },
    onSuccess: (data) => {
      if (!data.error) {
        toast.success("OTP verified successfully!");
        toast.info('A mail with reset password link has been sent to your mail address')
        // navigate(`/reset-password?token=${data?.data?.token}`);
        navigate("/login");
      } else {
        toast.error(data?.message, "Invalid OTP");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to verify OTP");
    },
  });

  const sendOtp = useMutation({
    mutationFn: ({ data }) => {
      return resendOtp(data);
    },
    onSuccess: (data) => {
      if (!data?.error) {
        toast.success("OTP resent successfully!");
        reset();
        if (data?.data?.token) {
          setSearchParams({ token: data.data.token });
        }
      } else {
        toast.error(data?.message, "Failed to resend OTP");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resend OTP");
    },
  });

  const onSubmit = (data) => {
    mutation.mutate({ data: { token: otpToken, otp: data.otp } });
  };

  const onResendOtp = () => {
    sendOtp.mutate({ data: { token: otpToken } });
    toast.info("OTP resent to your email/phone");
    reset();
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            OTP Confirmation
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter the 6-digit code sent to your email or phone.
          </p>

          <div className="mt-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="otp"
                  className="block text-sm font-medium text-gray-700"
                >
                  One-Time Password (OTP)
                </label>
                <div className="mt-1 relative">
                  <input
                    id="otp"
                    type="text"
                    maxLength={6}
                    {...register("otp")}
                    className="appearance-none block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </div>
                {errors.otp && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.otp.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={onResendOtp}
                  disabled={mutation.isLoading}
                  className="text-sm text-primary hover:text-primary-700 focus:outline-none"
                >
                  <RefreshCw className="inline-block mr-1 h-4 w-4" />
                  Resend OTP
                </button>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={mutation.isLoading}
                  className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {mutation.isLoading ? (
                    "Verifying..."
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Confirm OTP
                    </>
                  )}
                </button>
                
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Side Graphic */}
      <div className="hidden lg:block relative w-0 flex-1 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="absolute inset-0 flex flex-col justify-center items-center p-12 text-white">
          <h1 className="text-4xl font-bold mb-4 text-center">
            ZUBA
            <br />
            Admin Panel
          </h1>
          <p className="text-lg max-w-md text-center text-indigo-100">
            Manage your pet virtual assistant app, users, subscriptions, and
            more from this centralized dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OtpConfirmation;
